import { formatNumber, formatDate } from "@/lib/utils";
import { getCompanyConfig } from "@/config/company";
import { PrintDocumentTitle, PrintInfoGrid } from "@/components/shared/print";
import { numberToVietnameseWords } from "@/lib/numberToWords";

interface DeliveryPrintTemplateProps {
    delivery?: any;
    config?: any;
    showImages?: boolean;
}

export default function DeliveryPrintTemplate({ delivery = {}, config, showImages = false }: DeliveryPrintTemplateProps) {
    const c = getCompanyConfig(config);
    const docId = delivery?.deliveryId || delivery?.id || '';
    const customer = delivery?.customer || {};
    const items = delivery?.deliveryItems || [];

    const goodsItems = items.filter(
        (it: any) => !(it.isServiceItem && (it.serviceType === "VAT" || (it.productName && it.productName.toLowerCase().includes("vat"))))
    );
    const vatItem = items.find(
        (it: any) => it.isServiceItem && (it.serviceType === "VAT" || (it.productName && it.productName.toLowerCase().includes("vat")))
    );
    const otherServiceItems = items.filter(
        (it: any) => it.isServiceItem && !(it.serviceType === "VAT" || (it.productName && it.productName.toLowerCase().includes("vat")))
    );

    const goodsAmount = goodsItems.reduce((s: number, it: any) => s + Number(it.amount || 0), 0);
    const vatAmount = vatItem ? Number(vatItem.amount || 0) : 0;
    const deliveryFee = Number(delivery?.deliveryFee || 0);
    const grandTotal = (delivery?.totalAmount !== undefined && delivery?.totalAmount !== null && Number(delivery.totalAmount) > 0)
        ? Number(delivery.totalAmount)
        : (goodsAmount + vatAmount + deliveryFee);

    return (
        <div className="kh-print-doc p-[10mm] text-black bg-white w-full mx-auto min-h-[210mm]" style={{ fontFamily: "'Times New Roman', Times, serif" }} id="print-area">
            <style>{`
                @media print {
                    @page { size: A4 landscape; margin: 10mm; }
                    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                    #print-area { max-width: none !important; margin: 0 !important; }
                }
                .delivery-table td, .delivery-table th { border: 1px solid #000; padding: 6px; }
                .bg-gray-header { background-color: #f1f5f9; !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            `}</style>

            {/* HEADER */}
            <div className="flex justify-between items-start mb-4 pb-3 border-b-2 border-slate-900">
                <div className="flex items-center gap-4">
                    <img src={c.logoUrl} alt="Logo" className="h-14 w-auto object-contain flex-shrink-0" />
                    <div className="flex-1 overflow-hidden">
                        <h2 className="text-[14px] font-bold text-blue-900 uppercase whitespace-nowrap">{c.companyName}</h2>
                        <div className="text-[11px] text-slate-700 mt-0.5 whitespace-nowrap">{c.address1}</div>
                        <div className="text-[11px] text-slate-700 whitespace-nowrap">MST: {c.taxCode} - Hotline: {c.phone}</div>
                    </div>
                </div>
                <div className="text-right flex flex-col items-end justify-center">
                    <div className="border border-slate-300 rounded px-2 py-1 text-right bg-slate-50 text-[10px] text-slate-600">
                        <div className="font-bold text-slate-800">BM-GH-01</div>
                        <div>Số: <span className="font-mono font-bold text-slate-900">{docId || '—'}</span></div>
                    </div>
                </div>
            </div>

            {/* TITLE (CENTERED, NO QR) */}
            <PrintDocumentTitle
                title="PHIẾU GIAO HÀNG"
                subtitle="DELIVERY NOTE"
                docNo={docId}
                date={delivery.deliveryDate || new Date()}
                dateLabel="Ngày giao"
                extraMeta={
                    <span>Mã Đơn Bán: <strong className="font-mono text-indigo-900">{delivery.order?.orderId || delivery.orderId || '—'}</strong></span>
                }
            />

            {/* INFO (BORDERLESS 2-COL TABS) */}
            <div className="mb-4">
                <PrintInfoGrid
                    labelWidth="w-[130px]"
                    leftColumn={[
                        {
                            label: 'Khách Hàng',
                            value: customer.name || customer.shortName || '—',
                            isBold: true,
                        },
                        {
                            label: 'Địa Chỉ Giao',
                            value: delivery.address || customer.address || 'Kho Khách Hàng',
                        },
                        {
                            label: 'Người Liên Hệ',
                            value: `${delivery.receiverName || 'Đại Diện Khách Hàng'}${delivery.receiverPhone ? ` (${delivery.receiverPhone})` : ''}`,
                        },
                    ]}
                    rightColumn={[
                        {
                            label: 'Phương Tiện',
                            value: delivery.vehicleNumber || '—',
                            isBold: true,
                        },
                        {
                            label: 'Lái Xe / Giao Nhận',
                            value: delivery.driverName || 'Đội Xe APV Logistics',
                        },
                        {
                            label: 'Ghi Chú',
                            value: delivery.notes || 'Hàng mới 100%, đúng quy cách mẫu duyệt',
                        },
                    ]}
                />
            </div>

            {/* TABLE */}
            <table className="delivery-table w-full border-collapse text-[11px] mb-3 text-center">
                <thead>
                    <tr className="bg-gray-header font-bold text-[11px] text-slate-800">
                        <th className="w-[35px]">STT</th>
                        <th className="text-left">Tên Sản Phẩm Bao Bì & Dịch Vụ</th>
                        <th className="w-[60px]">ĐVT</th>
                        <th className="w-[80px]">SL Đặt</th>
                        <th className="w-[85px]">SL Thực Giao</th>
                        <th className="w-[90px]">Đơn Giá (VNĐ)</th>
                        <th className="w-[110px]">Thành Tiền (VNĐ)</th>
                        <th className="text-left">Quy Cách Đóng Thùng / Ghi Chú</th>
                    </tr>
                </thead>
                <tbody>
                    {/* 1. HÀNG HÓA SẢN PHẨM THỰC TẾ */}
                    {goodsItems.length === 0 ? (
                        <tr>
                            <td colSpan={8} className="py-4 italic text-slate-500">Chưa có sản phẩm nào được giao</td>
                        </tr>
                    ) : goodsItems.map((item: any, idx: number) => {
                        const isMissing = (Number(item.deliveredQty) || 0) < (Number(item.quantity) || 0);
                        const isExcess = (Number(item.deliveredQty) || 0) > (Number(item.quantity) || 0);
                        const deliveredCellClass = `font-bold font-mono ${
                            isMissing ? 'bg-red-50 text-red-700' :
                            isExcess ? 'bg-orange-50 text-orange-700' :
                            'text-indigo-900'
                        }`;
                        const rawSpecs = item.product?.specs || item.orderDetail?.specs || item.specs || {};
                        const parsedSpecs = typeof rawSpecs === 'string'
                            ? (() => { try { return JSON.parse(rawSpecs); } catch { return {}; } })()
                            : rawSpecs;
                        const fscCode =
                            item.fscCode ||
                            (parsedSpecs?.fscCode && parsedSpecs.fscCode !== 'NONE'
                                ? String(parsedSpecs.fscCode)
                                : null);

                        return (
                            <tr key={`item-${idx}`} className="break-inside-avoid hover:bg-slate-50/30">
                                <td>{idx + 1}</td>
                                <td className="text-left font-semibold text-slate-900">
                                    <span>{item.productName || item.product?.name || 'Hộp Bao Bì Offset'}</span>
                                    {fscCode && (
                                        <span
                                            className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-300"
                                            data-testid="delivery-print-fsc-badge"
                                        >
                                            [🌲 {fscCode}]
                                        </span>
                                    )}
                                </td>
                                <td className="whitespace-nowrap">{item.unit || item.product?.unit || 'Hộp'}</td>
                                <td className="font-mono whitespace-nowrap">{formatNumber(item.quantity || 0)}</td>
                                <td className={`${deliveredCellClass} whitespace-nowrap`}>{formatNumber(item.deliveredQty || 0)}</td>
                                <td className="font-mono text-right whitespace-nowrap">{item.unitPrice ? formatNumber(item.unitPrice) : '-'}</td>
                                <td className="font-mono font-bold text-right whitespace-nowrap">{item.amount ? formatNumber(item.amount) : '-'}</td>
                                <td className="text-left text-slate-700">{item.notes || 'Đóng thùng carton 5 lớp'}</td>
                            </tr>
                        );
                    })}

                    {/* 2. CÁC DÒNG DỊCH VỤ PHỤ TRÍ (NẾU CÓ) */}
                    {otherServiceItems.map((sItem: any, sIdx: number) => (
                        <tr key={`srv-${sIdx}`} className="break-inside-avoid italic bg-purple-50/20">
                            <td>{goodsItems.length + sIdx + 1}</td>
                            <td className="text-left font-semibold text-purple-900">[Dịch vụ] {sItem.productName}</td>
                            <td>{sItem.unit || "Gói"}</td>
                            <td className="text-slate-400">—</td>
                            <td className="font-mono font-bold text-purple-900">{formatNumber(sItem.deliveredQty || 1)}</td>
                            <td className="font-mono text-right">{sItem.unitPrice ? formatNumber(sItem.unitPrice) : '-'}</td>
                            <td className="font-mono font-bold text-right text-purple-900">{sItem.amount ? formatNumber(sItem.amount) : '-'}</td>
                            <td className="text-left text-slate-600">{sItem.notes || 'Phụ phí dịch vụ theo đơn'}</td>
                        </tr>
                    ))}

                    {/* 3. TỔNG KẾT TÀI CHÍNH Ở CHÂN BẢNG */}
                    <tr className="bg-gray-header font-bold text-[11px]">
                        <td colSpan={6} className="text-right uppercase px-3 py-1.5">
                            CỘNG TIỀN HÀNG (CHƯA BAO GỒM VAT):
                        </td>
                        <td className="font-mono font-black text-right px-2 py-1.5 text-slate-900">
                            {formatNumber(goodsAmount)}
                        </td>
                        <td className="text-left text-slate-500 font-normal">VNĐ</td>
                    </tr>

                    {vatItem && (
                        <tr className="font-bold text-[11px] bg-slate-50/60">
                            <td colSpan={6} className="text-right px-3 py-1 text-indigo-900">
                                THUẾ GIÁ TRỊ GIA TĂNG ({vatItem.productName}):
                            </td>
                            <td className="font-mono font-black text-right px-2 py-1 text-indigo-900">
                                {formatNumber(vatAmount)}
                            </td>
                            <td className="text-left text-indigo-700 font-normal">VNĐ</td>
                        </tr>
                    )}

                    {deliveryFee > 0 && (
                        <tr className="font-bold text-[11px] bg-slate-50/60">
                            <td colSpan={6} className="text-right px-3 py-1 text-slate-800">
                                PHÍ VẬN CHUYỂN & BỐC XẾP:
                            </td>
                            <td className="font-mono font-black text-right px-2 py-1 text-slate-900">
                                {formatNumber(deliveryFee)}
                            </td>
                            <td className="text-left text-slate-500 font-normal">VNĐ</td>
                        </tr>
                    )}

                    <tr className="bg-slate-100 font-black text-[12px] border-t-2 border-slate-900">
                        <td colSpan={6} className="text-right uppercase px-3 py-2 text-slate-950">
                            TỔNG CỘNG THANH TOÁN (VNĐ):
                        </td>
                        <td className="font-mono font-black text-right px-2 py-2 text-blue-950 text-[13px]">
                            {formatNumber(grandTotal)}
                        </td>
                        <td className="text-left font-bold text-slate-900">VNĐ</td>
                    </tr>
                </tbody>
            </table>

            {/* IN WORDS FINANCIAL NOTE */}
            {grandTotal > 0 && (
                <div className="mb-4 text-[11px] text-slate-800">
                    <strong>* Số tiền viết bằng chữ:</strong> <em>{numberToVietnameseWords(grandTotal)}</em>
                </div>
            )}

            {/* TERMS & SIGNATURES */}
            <div className="mb-4 text-[11px] text-slate-600 italic">
                * Cam kết: Hàng hóa được giao đúng số lượng, đúng quy cách theo đơn hàng và đạt tiêu chuẩn chất lượng xuất xưởng APV.
            </div>

            <div className="grid grid-cols-4 mt-6 text-[11px] text-center px-2 break-inside-avoid border-t border-slate-300 pt-3">
                <div>
                    <div className="font-bold text-slate-800 mb-12">NGƯỜI LẬP PHIẾU</div>
                    <div className="text-slate-500 italic">(Ký, ghi rõ họ tên)</div>
                    <div className="font-medium text-slate-700 mt-1">Phòng Kế Hoạch / Sales</div>
                </div>
                <div>
                    <div className="font-bold text-slate-800 mb-12">THỦ KHO XUẤT</div>
                    <div className="text-slate-500 italic">(Ký, ghi rõ họ tên)</div>
                    <div className="font-medium text-slate-700 mt-1">Bộ Phận Kho Thành Phẩm</div>
                </div>
                <div>
                    <div className="font-bold text-slate-800 mb-12">LÁI XE / GIAO NHẬN</div>
                    <div className="text-slate-500 italic">(Ký, ghi rõ họ tên)</div>
                    <div className="font-medium text-slate-700 mt-1">Đội Xe Vận Chuyển</div>
                </div>
                <div>
                    <div className="font-bold text-slate-800 mb-12">ĐẠI DIỆN KHÁCH HÀNG</div>
                    <div className="text-slate-500 italic">(Ký, nhận đủ hàng)</div>
                    <div className="font-medium text-slate-700 mt-1">Bên Mua Hàng</div>
                </div>
            </div>
        </div>
    );
}
